import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Search, UploadCloud, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

export default function AddRecipientsDialog({ open, onOpenChange, campaignId, onAdded }) {
  const [tab, setTab] = useState('pick');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111110] border-zinc-800 text-zinc-100 max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">Add recipients</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#0a0a0b] border border-zinc-900">
            <TabsTrigger value="pick" className="data-[state=active]:bg-[#1a1815] data-[state=active]:text-[#f5d67a]">Pick from contacts</TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-[#1a1815] data-[state=active]:text-[#f5d67a]">Import from file</TabsTrigger>
            <TabsTrigger value="manual" className="data-[state=active]:bg-[#1a1815] data-[state=active]:text-[#f5d67a]">Manual add</TabsTrigger>
          </TabsList>
          <TabsContent value="pick" className="pt-4"><PickPane campaignId={campaignId} onAdded={onAdded} onClose={()=>onOpenChange(false)}/></TabsContent>
          <TabsContent value="import" className="pt-4"><ImportPane campaignId={campaignId} onAdded={onAdded} onClose={()=>onOpenChange(false)}/></TabsContent>
          <TabsContent value="manual" className="pt-4"><ManualPane campaignId={campaignId} onAdded={onAdded} onClose={()=>onOpenChange(false)}/></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function PickPane({ campaignId, onAdded, onClose }) {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/contacts').then(r => setAll(r.data)).catch(()=>{}); }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return all.filter(c => c.campaign_id !== campaignId).filter(c =>
      !s || `${c.first_name} ${c.last_name} ${c.email} ${c.company}`.toLowerCase().includes(s)
    );
  }, [all, search, campaignId]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(filtered.map(c => c.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return toast.error('Pick at least one contact');
    setLoading(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/contacts`, { contact_ids: Array.from(selected) });
      toast.success(`${r.data.assigned} recipients added`);
      onAdded?.();
      onClose?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to add'));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <Input placeholder="Search your contacts..." value={search} onChange={e=>setSearch(e.target.value)} className="pl-9 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
        </div>
        <button type="button" onClick={selected.size === filtered.length ? clearAll : selectAll} className="text-xs text-[#f5d67a] hover:underline whitespace-nowrap">
          {selected.size === filtered.length && filtered.length > 0 ? 'Clear' : 'Select all'}
        </button>
      </div>
      <div className="mt-3 max-h-[380px] overflow-y-auto rounded-lg border border-zinc-900">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-zinc-500">No unassigned contacts. Try "Import" or "Manual add".</div>
        )}
        {filtered.map(c => (
          <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30 cursor-pointer">
            <input type="checkbox" checked={selected.has(c.id)} onChange={()=>toggle(c.id)} className="accent-[#d4a94a]"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-100 truncate">{c.first_name} {c.last_name}</div>
              <div className="text-xs text-zinc-500 truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</div>
            </div>
          </label>
        ))}
      </div>
      <DialogFooter className="mt-4">
        <div className="text-xs text-zinc-500 mr-auto self-center">{selected.size} selected</div>
        <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={onClose}>Cancel</Button>
        <Button disabled={loading || selected.size === 0} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={submit}><Check size={14} className="mr-1"/>Add {selected.size || ''}</Button>
      </DialogFooter>
    </div>
  );
}

function ImportPane({ campaignId, onAdded, onClose }) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | parsing | review
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPhase('parsing');
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/imports/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContacts(r.data.contacts.map(c => ({...c, selected: true})));
      setPhase('review');
      if (r.data.contacts.length === 0) toast.error('No contacts extracted');
    } catch (err) { toast.error(extractErrorMessage(err, 'Parsing failed')); setPhase('idle'); }
  };

  const submit = async () => {
    const chosen = contacts.filter(c => c.selected).map(c => ({
      first_name: c.first_name||'', last_name: c.last_name||'', email: c.email||'', phone: c.phone||'', company: c.company||'', job_title: c.job_title||'', status: 'queued'
    }));
    if (chosen.length === 0) return toast.error('Select at least one');
    setLoading(true);
    try {
      const r = await api.post(`/campaigns/${campaignId}/contacts`, { contacts: chosen });
      toast.success(`${r.data.assigned} recipients added`);
      onAdded?.(); onClose?.();
    } catch (err) { toast.error(extractErrorMessage(err, 'Failed')); } finally { setLoading(false); }
  };

  return (
    <div>
      {phase === 'idle' && (
        <label className="block cursor-pointer">
          <div className="border border-dashed border-zinc-800 rounded-lg p-8 text-center hover:border-[#d4a94a] transition-colors">
            <UploadCloud size={22} className="mx-auto text-[#f5d67a]"/>
            <div className="mt-3 font-display text-lg">Drop a CV, DOCX or CSV</div>
            <div className="text-xs text-zinc-500 mt-1">Contacts will be added directly to this campaign after you approve.</div>
            <input type="file" accept=".pdf,.docx,.csv,.txt" onChange={onFile} className="hidden"/>
          </div>
        </label>
      )}
      {phase === 'parsing' && (
        <div className="p-8 text-center text-sm text-zinc-400">Extracting contacts from {file?.name}…</div>
      )}
      {phase === 'review' && (
        <div>
          <div className="text-sm text-zinc-400 mb-2">{contacts.filter(c=>c.selected).length} of {contacts.length} selected</div>
          <div className="max-h-[280px] overflow-y-auto rounded-lg border border-zinc-900">
            {contacts.map(c => (
              <label key={c.id} className="flex items-center gap-3 px-4 py-2 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30 cursor-pointer">
                <input type="checkbox" checked={c.selected} onChange={()=>setContacts(cs => cs.map(x => x.id===c.id ? {...x, selected: !x.selected} : x))} className="accent-[#d4a94a]"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.email}{c.company ? ` · ${c.company}` : ''}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={()=>{setPhase('idle');setFile(null);setContacts([]);}}>Back</Button>
            <Button disabled={loading} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={submit}><Check size={14} className="mr-1"/>Add to campaign</Button>
          </DialogFooter>
        </div>
      )}
    </div>
  );
}

function ManualPane({ campaignId, onAdded, onClose }) {
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', phone:'', company:'', job_title:'' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!form.email) return toast.error('Email required');
    setLoading(true);
    try {
      await api.post(`/campaigns/${campaignId}/contacts`, { contacts: [{...form, status: 'queued'}] });
      toast.success('Recipient added');
      onAdded?.(); onClose?.();
    } catch (err) { toast.error(extractErrorMessage(err, 'Failed')); } finally { setLoading(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {[
        ['first_name','First name'], ['last_name','Last name'],
        ['email','Email *'], ['phone','Phone'],
        ['company','Company'], ['job_title','Job title'],
      ].map(([k, label]) => (
        <div key={k}>
          <Label className="text-zinc-300 text-xs">{label}</Label>
          <Input value={form[k]} onChange={e=>setForm({...form, [k]:e.target.value})} className="mt-1 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
        </div>
      ))}
      <div className="md:col-span-2 flex justify-end gap-2 mt-2">
        <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={onClose}>Cancel</Button>
        <Button disabled={loading} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={submit}>Add recipient</Button>
      </div>
    </div>
  );
}
